import { Component, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import { AuthService } from '../service/auth/auth-service.service';
import {
    BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  debounceTime,
  first,
  map,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DataList, FormSelectComponent } from './form-components/formselect/formselect.component';
import { FormControlComponent } from './form-components/formcontrol/formcontrol.component';
import { Router } from '@angular/router';

import {
  HttpService,
  IApiResponseResult,
  IApiResponseResultError,
} from '../service/http/httpservice.service';
import { NgbCalendar,  NgbDateNativeAdapter, NgbDateStruct, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { IconsModule } from '../icons/icons.module';
import { EmployeeService } from '../service/employee/employee.service';
import { BranchAvailabilitySet } from '../models/types/branch-availability';
import { Employee } from '../models/types/employee';
import {
  FormResults,
  NewEmployeeFormComponent,
} from './form-components/forms/new-employee-form/new-employee-form.component';
import { AddressService } from '../service/address/address.service';
import { AddressComponent } from '../models/types/address-types';
import { AddressModalComponent } from '../service/address/component/address/address.component';

@Component({
  selector: 'app-lead-component',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    FormSelectComponent,
    FormControlComponent,
    NgbDatepickerModule,
    IconsModule,
    NewEmployeeFormComponent,
    AddressModalComponent 
  ],
  providers: [NgbDateNativeAdapter],
  templateUrl: './lead-component.component.html',
  styleUrl: './lead-component.component.scss',
})
export class LeadComponent implements OnInit, OnDestroy {
  dto!: InitialDTO;
  formSubmitted = false;
  formSubmittedSuccessfully = false;
  employeeSaved = false;
  authService!: AuthService;
  router: Router;
  calendarService: NgbCalendar = inject(NgbCalendar);
  httpService = inject(HttpService);
  addressService = inject(AddressService);
  dateAdapterService = inject(NgbDateNativeAdapter);
  employeeFormErrors: string[] = [];
  showFormError = false;
  showVehicleTypeError = false;
  submitting = false;
  savedEmployee: IEmployeeModel | null = null;
  currentBranchAvailabilities: Record<string, BranchAvailabilitySet[]> | null = null;
  currentAvailableDates: string[] | null = null;
  currentAvailableTimes: BranchAvailabilitySet[] | null = null;
  availableTimes!: DataList[];
  employeeService: EmployeeService;
  formBuilder: FormBuilder = inject(FormBuilder);
  branchIdSignal!: Signal<number | null>;
  form$: Observable<Employee> = new Observable();

  constructor(authService: AuthService, router: Router, employeeService: EmployeeService) {
    this.authService = authService;
    this.router = router;
    this.employeeService = employeeService;
    this.dtoSub$ = this.getDtoRequest().subscribe({
      next: (response) => {
        this.dto = response!;
      },
      error: (error) => {
        this.employeeFormErrors.push(
          'There was an error loading the form. Autoland IT Helpdesk has been notified and will resolve the issue shortly. Please try again later.'
        );
      },
    });
    this.branchIdSignal = toSignal(this.leadForm.controls.branchId.valueChanges, {
      initialValue: null,
    });

    //this.initializeForm();
  }

  /**
   * Method to subscribe to the new employee form eventemitter.
   * @param formResults
   */
  newEmployee$ = (formResults: FormResults) => {
    console.log(formResults);
    if (formResults.errors) {
      console.log(formResults.errors);
    }
    const existingDtoIndex = this.dto.employees.findIndex((e) => e.id == formResults.values?.id);
    if (existingDtoIndex != -1) {
      this.dto.employees[existingDtoIndex] = formResults.values!;
    }
    this.leadForm.controls.employeeId.setValue(formResults.values?.id!);
  };

  leadForm = new FormGroup({
    branchId: new FormControl(0, {
      validators: [Validators.required, Validators.min(1)],
      updateOn: 'change',
      nonNullable: false,
    }),
    employeeId: new FormControl<number>(0, {
      validators: [Validators.required, Validators.min(1)],
      updateOn: 'change',
      nonNullable: true,
    }),
    employeeNotListed: new FormControl(false),
    newMember: new FormGroup({
      firstName: new FormControl('', {
        validators: [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(/^[\p{L}\-']+$/u),
        ], //regex: only letters, hyphens, and apostrophes from start to finish.
        updateOn: 'change',
        nonNullable: true,
      }),
      lastName: new FormControl('', {
        validators: [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(/^[\p{L}\-']+$/u),
        ], //regex: only letters, hyphens, and apostrophes from start to finish.
        updateOn: 'change',
        nonNullable: true,
      }),
      middleName: new FormControl(null, {
        validators: [
          Validators.pattern(/^(?!.*\..*\.)[\p{L}\-'\.]{1,50}$/u),
          Validators.maxLength(30),
        ], //regex: only letters, hyphens, periods and apostrophes from start to finish.
      }),
      email: new FormControl('', {
        validators: [Validators.email, Validators.maxLength(50), Validators.required],
        updateOn: 'change',
        nonNullable: true,
      }),
      primaryPhone: new FormControl('', {
        validators: [Validators.required, Validators.pattern(/^[2-9]\d{9}$/)], //regex: only numbers, can't start from 1 and can only be 9 digits
        updateOn: 'change',
        nonNullable: true,
      }),
      secondaryPhone: new FormControl(null, {
        validators: [Validators.pattern(/^[2-9]\d{9}$/)],
      }),
      address: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(100)],
        updateOn: 'change',
        nonNullable: true,
      }),
      address2: new FormControl('', {
        validators: [Validators.maxLength(20)],
        updateOn: 'change',
        nonNullable: true,
      }),
      city: new FormControl('', {
        validators: [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(/^[\p{L}\- ']+$/u),
        ], //regex: only letters, hyphens, and apostrophes from start to finish.
        updateOn: 'change',
        nonNullable: true,
      }),
      state: new FormControl('CA', {
        validators: [Validators.required, Validators.maxLength(2)],
        updateOn: 'change',
        nonNullable: false,
      }),
      zipCode: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(12)],
        updateOn: 'change',
        nonNullable: true,
      }),
      isValidated: new FormControl(false, { validators: [Validators.requiredTrue] }),
      isPreApproved: new FormControl(false, { nonNullable: true }),
      preApprovedAmount: new FormControl(null, {
        validators: [
          Validators.pattern(/^([1-9]\d*)?$/)         
        ],
        updateOn: 'change',
        nonNullable: true,
      }),
      preferredDateTime: new FormControl<NgbDateStruct | null>(null, {
        updateOn: 'change',
        nonNullable: false,
      }),
      preferredTime: new FormControl('', {
        updateOn: 'change',
        nonNullable: true,
      }),
      isVehicleTypeNew: new FormControl(false, {
        validators: [Validators.required],
        updateOn: 'change',
        nonNullable: true,
      }),
      isVehicleTypeUsed: new FormControl(false, {
        validators: [Validators.required],
        updateOn: 'change',
        nonNullable: true,
      }),
      isVehicleTypeTrade: new FormControl(false, {
        validators: [Validators.required],
        updateOn: 'change',
        nonNullable: true,
      }),
      notes: new FormControl(null),
    }),
  });
  newMemberForm = this.leadForm.controls.newMember;

  private destroy$ = new Subject<void>();
  private dtoSub$: Subscription | undefined;
  private saveLeadSub$: Subscription | undefined;
  private saveEmployeeSub$: Subscription | undefined;
  private updateEmployeeSub$: Subscription | undefined;
  private branchAvailabilitySub$: Subscription | undefined;
  availableTimes$?: BehaviorSubject<BranchAvailabilitySet[] | null> = new BehaviorSubject<BranchAvailabilitySet[] | null>(null);
  availableDates$?: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);

  validateAddress() {
    const addressLine1 = this.newMemberForm.controls.address.value;
    const addressLine2 = this.newMemberForm.controls.address2.value;
    const city = this.newMemberForm.controls.city.value;
    const state = this.newMemberForm.controls.state.value;
    const zipCode = this.newMemberForm.controls.zipCode.value;

    const fullAddress: AddressComponent = { addressLine1, addressLine2, city, state, zipCode };

    this.addressService.validateAddress(fullAddress).pipe(take(1)).subscribe();
  }

  ngOnInit() {
    this.leadForm.controls.branchId.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (value) => {
        if (value != null) {
          this.branchAvailabilitySub$ = this.onBranchAvailabilitiesRequest(value);
        }
      },
    });

    this.newMemberForm.controls.address.valueChanges.subscribe((next) => {
      this.newMemberForm.controls.isValidated.setValue(false);
    });
    this.newMemberForm.controls.address2.valueChanges.subscribe((next) => {
      this.newMemberForm.controls.isValidated.setValue(false);
    });
    this.newMemberForm.controls.city.valueChanges.subscribe((next) => {
      this.newMemberForm.controls.isValidated.setValue(false);
    });
    this.newMemberForm.controls.state.valueChanges.subscribe((next) => {
      this.newMemberForm.controls.isValidated.setValue(false);
    });
    this.newMemberForm.controls.zipCode.valueChanges.subscribe((next) => {
      this.newMemberForm.controls.isValidated.setValue(false);
    });

    this.leadForm.controls.newMember.controls.preferredDateTime.valueChanges.subscribe({
      next: (date) => {
        if (date == null) {
          return;
        }
      
        this.onBranchAvailabilitiesRequest(this.leadForm.controls.branchId.value!, this.dateAdapterService.toModel(date)!);

        this.newMemberForm.controls.preferredTime.reset();
        this.newMemberForm.controls.preferredTime.addValidators(Validators.required);
        this.newMemberForm.controls.preferredTime.markAsDirty();
        this.newMemberForm.controls.preferredTime.markAsTouched();
        this.newMemberForm.controls.preferredTime.updateValueAndValidity();

        //const key = `${date.year}-${date.month < 10 ? '0' + date.month : date.month}-${
        //  date.day < 10 ? '0' + date.day : date.day
        //  }`;

        //if (new Date(key).getDay() === 5)
        //{
        //  this.availableTimes = this.currentBranchAvailabilities![key].filter(x => new Date(x.fullDateTime).getHours() <= 12).map((x: any): DataList =>
        //  {
            
        //    return {
        //      id: x.availableDate,
        //      label: x.description,
        //      value: x.from,
        //    };
        //  });


        //}
        //else
        //{
        //  this.availableTimes = this.currentBranchAvailabilities![key].map((x: any): DataList =>
        //  {

        //    return {
        //      id: x.availableDate,
        //      label: x.description,
        //      value: x.from,
        //    };
        //  });
        //}

       
      },
    });

    // this.leadForm.controls.newMember.controls.address.valueChanges
    //   .pipe(
    //     tap(() => this.newMemberForm.controls.isValidated.setValue(false)),
    //     debounceTime(1000)
    //   )
    //   .subscribe({
    //     next: (value) => {
    //       const addressComponent: AddressComponent = {
    //         unformattedAddress: value,
    //       };

    //       this.addressService.validateAddress(addressComponent).subscribe({
    //         next: (result) => {
    //           console.log(result);
    //         },
    //       });
    //     },
    //   });

    this.addressService.confirmedAddress$.subscribe((address) => {
      this.newMemberForm.controls.address.setValue(address?.addressLine1!, { emitEvent: false });
      this.newMemberForm.controls.address2.setValue(address?.addressLine2!);
      this.newMemberForm.controls.city.setValue(address?.city!);
      this.newMemberForm.controls.state.setValue(address?.state!);
      this.newMemberForm.controls.zipCode.setValue(address?.fullZip!);
      this.newMemberForm.controls.isValidated.setValue(true);
    });

      this.leadForm.controls.newMember.controls.isVehicleTypeNew.valueChanges.subscribe((next) => {
         console.log(next)
        console.log(this.leadForm.controls.newMember.controls.isVehicleTypeNew)
        this.showVehicleTypeError = false;
      })

    this.leadForm.controls.newMember.controls.isVehicleTypeUsed.valueChanges.subscribe((next) => {
      console.log(next)
      console.log(this.leadForm.controls.newMember.controls.isVehicleTypeUsed)
      this.showVehicleTypeError = false;
    })

    this.leadForm.controls.newMember.controls.isVehicleTypeTrade.valueChanges.subscribe((next) => {
      console.log(next)
      console.log(this.leadForm.controls.newMember.controls.isVehicleTypeTrade)
      this.showVehicleTypeError = false;
    })

      // this.newMemberForm.controls.isPreApproved.valueChanges.subscribe((isPreApproved) => {
      //   const preApprovedAmountControl = this.newMemberForm.controls.preApprovedAmount;
  
      //   if (isPreApproved) {
      //     // preApprovedAmountControl.setValidators([Validators.required, Validators.pattern(/^[1-9]\d*$/)]);
      //     preApprovedAmountControl.setValidators([Validators.pattern(/^[1-9]\d*$/)]);
      //   } else {
      //     preApprovedAmountControl.clearValidators();
      //     preApprovedAmountControl.setValue(null);
      //   }
  
      //   preApprovedAmountControl.updateValueAndValidity();
      // });
  }

  ngOnDestroy(): void {
    console.log('destroy initiated');
    this.cancelRequest();
  }

  cancelRequest() {
    this.dtoSub$?.unsubscribe();
    this.saveLeadSub$?.unsubscribe();
    this.saveEmployeeSub$?.unsubscribe();
    this.updateEmployeeSub$?.unsubscribe();
    this.branchAvailabilitySub$?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }
  getDataList(data: any[]): DataList[] {
    return data.map((item) => {
      return item.dataList;
    });
  }

  onEmployeeDelete() {
    if (this.savedEmployee == null || this.savedEmployee.id == null) {
      this.employeeFormErrors.push('No employee selected for deletion.');
      return;
    }

    this.updateEmployeeSub$ = this.deleteEmployeeRequest(this.savedEmployee.id).subscribe({
      next: (response) => {
        if (response != null && response.isSuccess && response.value !== null) {
          const index = this.dto.employees.findIndex((x) => x.id == response.value!.id);
          if (index !== -1) {
            this.dto.employees.splice(index, 1);
            this.leadForm.controls.employeeId.setValue(-1);
            this.leadForm.controls.employeeNotListed.setValue(false);
            this.leadForm.controls.employeeId.enable();
            this.leadForm.controls.employeeId.updateValueAndValidity();
            this.leadForm.controls.employeeNotListed.updateValueAndValidity();

            this.savedEmployee = null;
            this.employeeSaved = false;
          }
        } else {
          throwError(
            () =>
              new Error(
                response.errors?.map((error: any) => error.errorDescription).join(', ') ||
                  'An error occurred while deleting the employee. Please try again later.'
              )
          );
        }
      },
      error: (error) => {
        this.employeeFormErrors.push(error);
      },
    });
  }

  onLeadSubmit() {
    this.formSubmitted = true;
    const newMember = this.leadForm.controls.newMember.controls;

    if (!newMember.isVehicleTypeNew.value && !newMember.isVehicleTypeUsed.value && !newMember.isVehicleTypeTrade.value) {
      this.leadForm.setErrors({ formInvalid: false });
      this.showVehicleTypeError = true;
      console.log('No vehicle type(s) selected.')
      return;
    }

    const preApprovedAmountNum = Number(newMember.preApprovedAmount.value);
    if (newMember.isPreApproved.value == true && Number.isInteger(preApprovedAmountNum) == false) {
      console.log('Pre-Approved Amount is not valid.');
      alert('Pre-Approved Amount is not valid.');
      return;
    }

    if (this.leadForm.valid) {
      this.submitting = true;       

      let vehicleType = '';

      if (newMember.isVehicleTypeNew.value) {
        if (vehicleType == '') {
          vehicleType = 'NewVehicle';
        }
      }

      if (newMember.isVehicleTypeUsed.value) {
        if (vehicleType == '') {
          vehicleType = 'UsedVehicle';
        } else {
          vehicleType += ',UsedVehicle';
        }
      }

      if (newMember.isVehicleTypeTrade.value) {
        if (vehicleType == '') {
          vehicleType = 'TradeVehicle';
        } else {
          vehicleType += ',TradeVehicle';
        }
      }

      const dto: LeadModel = {
        affinityGroupId: this.dto.affinityGroupId,
        branchId: this.leadForm.controls.branchId.value,
        employeeId: this.leadForm.controls.employeeId.value,
        firstName: newMember.firstName.value,
        middleName: newMember.middleName.value,
        lastName: newMember.lastName.value,
        email: newMember.email.value,
        primaryPhone: newMember.primaryPhone.value,
        secondaryPhone: newMember.secondaryPhone.value,
        address: newMember.address.value,
        address2: newMember.address2.value,
        city: newMember.city.value,
        state: newMember.state.value,
        zipcode: newMember.zipCode.value,
        preferredDateTime: null,
        isPreApproved: newMember.isPreApproved.value,
        preApprovedAmount: newMember.preApprovedAmount.value == '' ? null : newMember.preApprovedAmount.value,
        vehicleType: vehicleType,
        notes: newMember.notes.value,
      };

      if (newMember.preferredDateTime.value == null || newMember.preferredTime.value == null) {
        dto.preferredDateTime = null;
      }
      else {
        dto.preferredDateTime = `${newMember.preferredDateTime.value?.month}/${newMember.preferredDateTime.value?.day}/${newMember.preferredDateTime.value?.year} ${newMember.preferredTime.value}`;
      }

      console.log(dto);
      //console.log('saved!!');

      //return;
    

      this.saveLeadSub$ = this.submitLeadRequest(dto).subscribe((response) => {
        this.submitting = false;

        if (response.isSuccess && response.value !== null) {
          this.formSubmittedSuccessfully = true;
        }
        else {
          this.showFormError = true;
        }
      });
    }
  }

  onBranchAvailabilitiesRequest(branchId: number, date?:Date) {
    if (!branchId) {
      return;
    }

    return this.getBranchAvailabilitiesRequest(branchId, date).subscribe({
      next: (response) => {
        if (response.result?.availableDates)
        {
          this.availableDates$?.next(response.result.availableDates);
        }

        if (response.result?.availableTimes)
        {
          this.availableTimes$?.next(response.result.availableTimes);
        }
      },
    });
  }

  //isDateDisabled = (date: NgbDateStruct) => {
  //  if (this.currentBranchAvailabilities) {
  //    const key = `${date.year}-${date.month < 10 ? '0' + date.month : date.month}-${
  //      date.day < 10 ? '0' + date.day : date.day
  //    }`;

  //    if (this.currentBranchAvailabilities[key]) {
  //      return false;
  //    }
  //  }

  //  return true;
  //};

  isDateDisabled = (date: NgbDateStruct) =>
  {

      const key = `${date.year}-${date.month < 10 ? '0' + date.month : date.month}-${date.day < 10 ? '0' + date.day : date.day
        }`;

    if (this.availableDates$?.value?.find(x=>x == key))
    {
      return false;
    }
    

    return true;
  };

  getDtoRequest() {
    return this.httpService.get<IApiResponseResult<InitialDTO>>('/api/lead/assets').pipe(
      map((response) => {
        if (response.value == null) {
          return response.value;
        }

        response.value.branches = response.value.branches?.map((branch: any) => {
          return {
            id: branch.id,
            label: branch.branchName,
            value: branch.id,
          } as DataList;
        });

        response.value.employees = response.value.employees
          ?.filter((e: any) => e.displayName.toString().trim() !== '')
          .map((employee: any) => {
            employee.dataList = {
              id: employee.id,
              label: employee.displayName,
              value: employee.id,
            };

            return employee;
          });
        response.value.states = response.value.states
          ?.filter((s: any) => s.toString().trim() !== '')
          .map((s: any) => {
            return { id: s, label: s, value: s } as DataList;
          });

        return response.value;
      })
    );

    //return this.http.get<AssetResult>("/api/lead/assets").pipe(
    //  map(response => {
    //    response.value.branches = response.value.branches?.map((branch: any) => {
    //      return { Id: branch.id, Label: branch.branchName, Value: branch.id } as DataList
    //    });
    //    response.value.employees = response.value.employees?.filter((e: any) => e.displayName.toString().trim() !== '').map((employee: any) => {
    //      return { Id: employee.id, Label: employee.displayName, Value: employee.id } as DataList
    //    });

    //    response.value.states = response.value.states?.filter((s: any) => s.toString().trim() !== '').map((s: any) => {
    //      return { Id: s, Label: s, Value: s } as DataList
    //    });

    //    return response;
    //  }));
  }

  submitLeadRequest(lead: LeadModel) {
    return this.httpService.post<IApiResponseResult<LeadModel>>(
      '/api/lead/saveform',
     lead
    );
  }

  saveEmployeeRequest(employeeModel: Employee) {
    return this.employeeService.saveEmployee(employeeModel);

    // return this.httpService.post<IApiResponseResult<IEmployeeModel>>(
    //   '/api/lead/employee',
    //   JSON.stringify(employeeModel)
    // );
  }

  deleteEmployeeRequest(employeeId: number) {
    return this.httpService.delete<IApiResponseResult<IEmployeeModel>>(
      `/api/lead/employee/${employeeId}`
    );
  }

  updateEmployeeRequest(employeeModel: IEmployeeModel) {
    return this.httpService.put<IApiResponseResult<IEmployeeModel>>(
      '/api/lead/employee',
      JSON.stringify(employeeModel)
    );
  }

  getBranchAvailabilitiesRequest(branchId: number, date?: Date) {
    return this.employeeService.getBranchAvailabilities(branchId, date).pipe(first());
  }

  logOut() {
    this.authService.logout().subscribe({
      next: (response) => {
        this.router.navigate(['login']);
      },
    });
  }
}

export type ShowValidationOptions =
  | 'OnFormSubmitOnly'
  | 'OnInvalidInputOnly'
  | 'OnInvalidInputAndFormSubmitOnly'
  | 'OnInvalidInputAndTouchedOnly';

interface LeadModel {
  affinityGroupId: number;
  branchId?: number | null;
  employeeId: number | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string;
  primaryPhone: string;
  secondaryPhone: string | null;
  address: string;
  address2: string;
  city: string;
  state: string | null;
  zipcode: string;
  isPreApproved: boolean;
  preApprovedAmount: number | null;
  preferredDateTime: string | NgbDateStruct | null;
  vehicleType: string;
  notes: string | null;
}

export interface IEmployeeModel {
  id?: number | null;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  email: string | null;
  primaryPhone: string | null;
  affinityGroupId: number | null;
  branchId: number | null;
  dataList?: DataList;
}

export interface InitialDTO {
  affinityGroupId: number;
  affinityGroupName: string;
  branches: Object[] | DataList[] | any;
  employees: IEmployeeModel[];
  states: Object[] | DataList[] | any;
}

export interface AssetResult {
  isSuccess: boolean;
  isPartialSuccess: boolean;
  isFailure: boolean;
  errors: IApiResponseResultError[] | null;
  value: InitialDTO;
}
